import {
  LeaveBalanceSnapshot,
  LeaveBalanceAdjustmentRecord,
  LeaveAdjustmentKind,
  LeaveTypeMaster,
} from '../types/leavePolicy';
import { LeaveRequest } from '../types/leave';
import { LeavePolicyService } from './leavePolicyService';
import { LeaveService } from './leave';
import { CompOffService } from './compOffService';
import { LeaveAuditService } from './leaveAuditService';
import { getAllUserEmails, loadAllUsers, SimpleUser } from './userLoader';
import { isResolvedIdentity, sameIdentity } from './identity';
import { leaveDaysFor } from './leaveDuration';
import { NamedLeavePolicy, AssignmentScopeType } from '../types/leavePolicy';
import { computeAllLeaveGrants, computeEarnedMonths } from './leaveAccrualEngine';
import { generateClient } from 'aws-amplify/api';

/**
 * Local cache of the server's adjustment ledger — NOT the system of record.
 *
 * Reads have to stay synchronous (computeSnapshotsFor is documented as pure and non-fetching,
 * and several screens read adjustments inline), so the same split Comp-Off uses applies here:
 * `refreshAdjustments()` pulls the server into this cache and every read below is a cache read.
 */
const ADJUSTMENTS_KEY = 'creyenteone:leave-adjustments:v1';

let adjustmentClient: any = null;
const getAdjustmentClient = () => {
  if (!adjustmentClient) adjustmentClient = generateClient();
  return adjustmentClient;
};

const adjustmentFields = `
  id employeeEmail leaveTypeCode year hrAdjustment adjustmentKind carryForward compOffEarned
  expiresOn reason updatedBy updatedAt
`;

/**
 * The same selection without `adjustmentKind`, for a schema that has not been applied yet.
 *
 * A selection set is validated against the schema *as deployed*, and this repo's AppSync schema is
 * applied by a Terraform run that is manual-dispatch in production. Asking for a field the deployed
 * API has never heard of is a validation error for the *whole query* — so without this fallback,
 * merging the frontend before running that apply would stop the adjustment ledger syncing for
 * everybody, and every balance would quietly fall back to whatever each browser had cached.
 *
 * Same approach, and the same reasoning, as ShiftRosterService's detailed-assignment selection.
 */
const adjustmentFieldsWithoutKind = `
  id employeeEmail leaveTypeCode year hrAdjustment carryForward compOffEarned
  expiresOn reason updatedBy updatedAt
`;

const listLeaveAdjustmentsQuery = /* GraphQL */ `
  query ListLeaveAdjustments($limit: Int, $nextToken: String) {
    listLeaveAdjustments(limit: $limit, nextToken: $nextToken) {
      items { ${adjustmentFields} }
      nextToken
    }
  }
`;

const listLeaveAdjustmentsLegacyQuery = /* GraphQL */ `
  query ListLeaveAdjustments($limit: Int, $nextToken: String) {
    listLeaveAdjustments(limit: $limit, nextToken: $nextToken) {
      items { ${adjustmentFieldsWithoutKind} }
      nextToken
    }
  }
`;

/**
 * Whether the deployed API serves `adjustmentKind`.
 *
 * Module-level and sticky in both directions it needs to be: once a request has been refused for
 * the unknown field, every later one would be refused identically, and a page that refreshes the
 * ledger on several events should not rediscover that each time.
 *
 * While false, rows read back carry no kind and are therefore classified by sign — exactly the
 * behaviour that predates the field. Writes drop it the same way, so nothing is stored that the
 * schema cannot hold.
 */
let adjustmentKindAvailable = true;

/** True when a GraphQL failure is the deployed schema refusing an unknown field. */
const isUnknownFieldError = (error: any): boolean => {
  const message = String(
    error?.errors?.[0]?.message || error?.message || ''
  );
  return /field|validation|unknown/i.test(message);
};

const createLeaveAdjustmentMutation = /* GraphQL */ `
  mutation CreateLeaveAdjustment($input: CreateLeaveAdjustmentInput!) {
    createLeaveAdjustment(input: $input) { ${adjustmentFields} }
  }
`;

/**
 * The kinds this build understands.
 *
 * A value outside this list is dropped to undefined and the row falls back to being read by sign,
 * rather than being trusted and silently landing on the wrong side of the balance. Guards against
 * a newer client writing a kind an older one has never heard of — the schema deliberately types
 * the field as String for the same reason.
 */
const KNOWN_ADJUSTMENT_KINDS: string[] = [
  'ENTITLEMENT_CREDIT',
  'ENTITLEMENT_REDUCTION',
  'OPENING_USED',
  'USED_REVERSAL',
];

/**
 * How each kind is named to a person — on the dialog, and on the audit trail.
 *
 * One definition, because a kind named one thing when HR picks it and another when they read it
 * back six months later is a kind nobody can reconcile.
 */
export const ADJUSTMENT_KIND_LABELS: Record<LeaveAdjustmentKind, string> = {
  ENTITLEMENT_CREDIT: 'Increase Leave Balance',
  ENTITLEMENT_REDUCTION: 'Reduce Leave Balance',
  OPENING_USED: 'Mark Leave as Used',
  USED_REVERSAL: 'Restore Used Leave',
};

/**
 * The kinds a given sign can legitimately carry, most likely first.
 *
 * Half the four are impossible for any given number — "Entitlement reduced: +2" is not a thing —
 * so every form offers only the possible pair, and the head of each pair is the behaviour these
 * forms had before kinds existed: a credit adds entitlement, a deduction records leave taken.
 *
 * Shared because three forms need the rule and a fourth will: if one of them offered a kind whose
 * sign it then wrote the other way, the days would land on the wrong side of the balance with
 * nothing on screen contradicting it.
 */
/**
 * Every kind, in the order a form should offer them: what days do, then what they undo.
 *
 * Offered as one list rather than filtered by the sign of a typed number, because asking for both
 * was the bug. The forms took a signed figure *and* a kind, so "28" with "Entitlement reduced"
 * selected was a contradiction — and the sign won, silently storing a credit. HR reduced an
 * entitlement by 28 and watched it grow by 28. The kind is the intent; a sign typed beside it is
 * a second, rival way of saying the same thing.
 */
export const ADJUSTMENT_KINDS: LeaveAdjustmentKind[] = [
  'ENTITLEMENT_CREDIT',
  'ENTITLEMENT_REDUCTION',
  'OPENING_USED',
  'USED_REVERSAL',
];

/** Whether a kind adds days to the balance. The other two take them away. */
export const isCreditKind = (kind: LeaveAdjustmentKind): boolean =>
  kind === 'ENTITLEMENT_CREDIT' || kind === 'USED_REVERSAL';

/**
 * The signed figure to store, from the kind and a magnitude.
 *
 * One direction, decided in one place. The magnitude is what a form collects — "how many days" —
 * and the kind alone says which way they move, so the two can no longer disagree. A sign typed
 * into the magnitude is ignored rather than fought over: `-28` under "Extra entitlement granted"
 * means 28 granted, which is the only reading of it that matches what was chosen.
 */
export const signedAdjustmentDays = (kind: LeaveAdjustmentKind, days: number): number => {
  const magnitude = Math.abs(days);
  return isCreditKind(kind) ? magnitude : -magnitude;
};

/**
 * The kind to actually store, guaranteed consistent with the sign it is stored against.
 *
 * Now that callers derive the sign from the kind via signedAdjustmentDays, this always agrees with
 * what they passed. It stays as the enforcement point: this is the only path to storage, and a row
 * whose days point the opposite way to its kind would be wrong for good rather than for one
 * render.
 */
export const resolveAdjustmentKind = (
  isCredit: boolean,
  chosen?: LeaveAdjustmentKind | ''
): LeaveAdjustmentKind => {
  if (chosen && isCreditKind(chosen) === isCredit) return chosen;
  return isCredit ? 'ENTITLEMENT_CREDIT' : 'OPENING_USED';
};

/** Coerce a row off the wire into the shape the balance maths expects. */
const normalizeAdjustment = (raw: any): LeaveBalanceAdjustmentRecord => ({
  id: String(raw?.id || ''),
  employeeEmail: String(raw?.employeeEmail || ''),
  leaveTypeCode: String(raw?.leaveTypeCode || ''),
  year: Number(raw?.year) || 0,
  carryForward: Number(raw?.carryForward) || 0,
  compOffEarned: Number(raw?.compOffEarned) || 0,
  hrAdjustment: Number(raw?.hrAdjustment) || 0,
  // Left undefined rather than defaulted, so a row written before the field existed stays
  // distinguishable from one HR deliberately classified — see adjustmentKindOf.
  adjustmentKind: KNOWN_ADJUSTMENT_KINDS.includes(raw?.adjustmentKind)
    ? (raw.adjustmentKind as LeaveAdjustmentKind)
    : undefined,
  expiresOn: raw?.expiresOn || undefined,
  reason: String(raw?.reason || ''),
  updatedBy: String(raw?.updatedBy || ''),
  updatedAt: String(raw?.updatedAt || ''),
});

/**
 * Leave types whose newly credited days cannot be spent on the day they are credited.
 *
 * Maternity and Paternity only. Deliberately a separate list from OPT_IN_TYPE_CODES even though
 * the two currently hold the same codes: that one answers "is this worth showing at zero", this
 * one answers "can this be spent immediately". Collapsing them would tie an accounting rule to a
 * display rule, and the next code added to either would silently inherit the other's behaviour.
 *
 * Comp-Off is absent on purpose — it enforces the same rule from its own ledger, keyed on the
 * approval timestamp rather than on an adjustment row. See CompOffService.isApprovedToday.
 */
const SAME_DAY_CREDIT_CODES = ['ML', 'PL'];

const isSameDayCreditCode = (code: string): boolean =>
  SAME_DAY_CREDIT_CODES.includes((code || '').toUpperCase());

/**
 * Whether an adjustment row was credited today, in the viewer's timezone.
 *
 * `updatedAt` is the only timestamp these rows carry — there is no createdAt — so an old credit
 * that HR edits today reads as new and is withheld for one day. Noted as a limitation rather than
 * worked around: erring towards withholding matches the rule's intent, and the alternative
 * (adding createdAt) needs a schema change and a backfill.
 */
const creditedToday = (
  adjustment: { updatedAt?: string },
  todayIso: string
): boolean => CompOffService.localDateOf(adjustment.updatedAt) === todayIso;

export class LeaveBalanceService {
  /**
   * Pull the whole adjustment ledger from the server into the local cache.
   *
   * Scans rather than querying by employee: the balance engine is asked about one person on a
   * dashboard and about everybody on Reports and Leave Assignment, and one scan serves both.
   *
   * Only writes and announces a change when the data actually differs — getBalances calls this,
   * and several pages recompute balances in response to 'leave-balance-updated', so firing
   * unconditionally would have those pages trigger the refresh that re-fires the event.
   */
  static async refreshAdjustments(): Promise<boolean> {
    try {
      let result: any;
      try {
        result = await getAdjustmentClient().graphql({
          query: adjustmentKindAvailable ? listLeaveAdjustmentsQuery : listLeaveAdjustmentsLegacyQuery,
          variables: { limit: 500 },
          authMode: 'userPool' as const,
        } as any);
      } catch (error: any) {
        // A field the deployed schema does not know is a validation error, not a data error:
        // retry once on the selection every deployed schema has, and remember the answer.
        if (!adjustmentKindAvailable || !isUnknownFieldError(error)) throw error;
        console.warn(
          '[LeaveBalance] adjustmentKind rejected by the deployed schema; reading the ledger '
          + 'without it. Adjustments will be classified by sign — a positive as extra allotment, '
          + 'a negative as leave taken before go-live — until the AppSync schema is applied.'
        );
        adjustmentKindAvailable = false;
        result = await getAdjustmentClient().graphql({
          query: listLeaveAdjustmentsLegacyQuery,
          variables: { limit: 500 },
          authMode: 'userPool' as const,
        } as any);
      }
      const items = ('data' in result ? result.data : null)?.listLeaveAdjustments?.items;
      if (!Array.isArray(items)) return false;

      const next = JSON.stringify(items.map(normalizeAdjustment));
      if (next !== localStorage.getItem(ADJUSTMENTS_KEY)) {
        localStorage.setItem(ADJUSTMENTS_KEY, next);
        window.dispatchEvent(new Event('leave-balance-updated'));
        window.dispatchEvent(new Event('storage'));
      }
      return true;
    } catch (error) {
      console.warn('[LeaveBalance] adjustment refresh failed, using cached ledger:', error);
      return false;
    }
  }

  /**
   * The HR grant/deduction ledger, optionally narrowed to one employee and year.
   *
   * Omitting `employeeEmail` reads the whole ledger, which is what the dedupe in addAdjustment
   * needs. Passing an *empty* one is a failed identity lookup rather than a request for
   * everybody, and must not widen the read: the previous `!employeeEmail ||` short-circuit let
   * a session with no email inherit every grant and deduction HR had ever recorded, for
   * anybody, straight into one person's balance. See isResolvedIdentity in identity.ts.
   */
  static getAdjustments(employeeEmail?: string, year?: number): LeaveBalanceAdjustmentRecord[] {
    if (employeeEmail !== undefined && !isResolvedIdentity(employeeEmail)) {
      console.warn(
        '[LeaveBalance] getAdjustments called with an empty employee identity — returning none.'
      );
      return [];
    }
    try {
      const raw = localStorage.getItem(ADJUSTMENTS_KEY);
      if (!raw) return [];
      const parsed: LeaveBalanceAdjustmentRecord[] = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(a => {
        const matchesEmail = employeeEmail === undefined || sameIdentity(a.employeeEmail, employeeEmail);
        const matchesYear = !year || a.year === year;
        return matchesEmail && matchesYear;
      });
    } catch {
      return [];
    }
  }

  /**
   * Record an HR grant or deduction. Server first.
   *
   * Awaited by callers now, because it is a network write: this used to be a synchronous
   * localStorage append, which meant HR granting an employee days wrote the entry to HR's own
   * browser and nowhere else. The employee's balance adds `hrAdjustment` from this ledger, so
   * on their machine the ledger was empty and the grant simply did not exist.
   *
   * The cache is updated whether or not the call succeeded, matching LeaveService: leaving it
   * stale would make the entry appear to vanish, and refreshAdjustments re-seeds it from the
   * server anyway. `ok` reports whether it actually reached the server, so a caller can tell
   * HR the difference between "saved" and "saved only here".
   */
  static async addAdjustment(
    employeeEmail: string,
    leaveTypeCode: string,
    year: number,
    adjustmentDays: number,
    updatedBy: string,
    reason: string,
    /** Optional last day the granted days may be used. Omit for no expiry. */
    expiresOn?: string,
    /**
     * Which side of the balance these days belong to.
     *
     * Omit and the row is stored unclassified, to be read by sign — the pre-existing behaviour,
     * kept so a caller that has no opinion writes exactly what it wrote before. Callers that do
     * know (the Adjust Balance dialog, HR Operations) pass it, which is what makes an entitlement
     * reduction distinguishable from leave taken before go-live.
     */
    adjustmentKind?: LeaveAdjustmentKind
  ): Promise<{ ok: boolean; record: LeaveBalanceAdjustmentRecord }> {
    /*
     * Clamped here as well as in the form, because this is the only path to storage.
     *
     * A caller holding a kind chosen under the opposite sign would otherwise persist a row whose
     * days move the wrong figure — and unlike a form slip, a stored row is wrong for good. Passing
     * nothing still stores nothing, so a caller with no opinion writes what it always wrote.
     */
    const kind = adjustmentKind
      ? resolveAdjustmentKind(adjustmentDays > 0, adjustmentKind)
      : undefined;

    let record: LeaveBalanceAdjustmentRecord | null = null;
    try {
      const result = await getAdjustmentClient().graphql({
        query: createLeaveAdjustmentMutation,
        variables: {
          input: {
            employeeEmail,
            leaveTypeCode,
            year,
            hrAdjustment: adjustmentDays,
            // Omitted while the deployed schema lacks the field: sending an input field the API
            // does not define fails the whole mutation, which would turn a classified adjustment
            // into no adjustment at all.
            adjustmentKind: adjustmentKindAvailable ? kind : undefined,
            carryForward: 0,
            compOffEarned: 0,
            expiresOn: expiresOn || undefined,
            reason,
            updatedBy,
          },
        },
        authMode: 'userPool' as const,
      } as any);
      const raw = ('data' in result ? result.data : null)?.createLeaveAdjustment;
      if (raw) record = normalizeAdjustment(raw);
    } catch (error) {
      console.warn('[LeaveBalance] createLeaveAdjustment failed, caching locally only:', error);
    }

    const reachedServer = record !== null;
    if (!record) {
      record = {
        id: `adj-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        employeeEmail,
        leaveTypeCode,
        year,
        carryForward: 0,
        compOffEarned: 0,
        hrAdjustment: adjustmentDays,
        adjustmentKind: kind,
        expiresOn: expiresOn || undefined,
        reason,
        updatedBy,
        updatedAt: new Date().toISOString(),
      };
    }

    const existing = this.getAdjustments();
    localStorage.setItem(ADJUSTMENTS_KEY, JSON.stringify([record, ...existing]));

    LeaveAuditService.logAction(
      record.id,
      'HR Leave Balance Adjustment',
      updatedBy,
      '0',
      // The kind goes on the trail too. Two rows of "-2 day(s) SL" that moved different figures
      // are indistinguishable afterwards without it, which is the whole point of recording a kind.
      `${adjustmentDays > 0 ? '+' : ''}${adjustmentDays} day(s) ${leaveTypeCode}`
        + `${kind ? ` (${ADJUSTMENT_KIND_LABELS[kind]})` : ''}`,
      reason,
      employeeEmail
    );

    window.dispatchEvent(new Event('leave-balance-updated'));
    window.dispatchEvent(new Event('storage'));
    return { ok: reachedServer, record };
  }

  /**
   * Days credited today that the balance is withholding until tomorrow, for one leave type.
   *
   * Exists so a screen can say why a grant the employee just watched HR make has not moved their
   * balance. Returns 0 for every leave type the rule does not cover, so a caller can ask about any
   * code without first checking whether it applies.
   */
  static getCreditedTodayDays(
    employeeEmail: string,
    leaveTypeCode: string,
    year: number
  ): number {
    if (!isSameDayCreditCode(leaveTypeCode)) return 0;
    const todayIso = CompOffService.todayIso();
    const code = leaveTypeCode.toUpperCase();
    return this.getAdjustments(employeeEmail, year)
      .filter(a => a.leaveTypeCode.toUpperCase() === code)
      .filter(a => !a.expiresOn || a.expiresOn >= todayIso)
      .filter(a => a.hrAdjustment > 0 && creditedToday(a, todayIso))
      .reduce((acc, a) => acc + a.hrAdjustment, 0);
  }

  /**
   * Which side of the balance a ledger row belongs to.
   *
   * The stored kind when HR set one; otherwise inferred from the sign — positive as a credit,
   * negative as leave taken before go-live. That fallback is not a guess: it is precisely how every
   * row behaved before the field existed, so the thousands already in the ledger keep reading the
   * way they read yesterday and no backfill is needed.
   */
  /**
   * Whether the deployed API stores the adjustment kind.
   *
   * False until the AppSync schema is applied, in which case every adjustment is read by sign and
   * an entitlement reduction cannot be distinguished from leave taken — worth saying out loud on a
   * form that offers the choice, rather than letting HR pick something with no effect.
   */
  static adjustmentKindSupported(): boolean {
    return adjustmentKindAvailable;
  }

  static adjustmentKindOf(adjustment: LeaveBalanceAdjustmentRecord): LeaveAdjustmentKind {
    if (adjustment.adjustmentKind) return adjustment.adjustmentKind;
    return adjustment.hrAdjustment >= 0 ? 'ENTITLEMENT_CREDIT' : 'OPENING_USED';
  }

  /**
   * Whether a row's days move the entitlement rather than the used figure.
   *
   * The two used-side kinds are the exception, so a kind this build does not recognise lands on the
   * entitlement side — which is where an unclassified row went before any of this existed.
   */
  static affectsEntitlement(adjustment: LeaveBalanceAdjustmentRecord): boolean {
    const kind = this.adjustmentKindOf(adjustment);
    return kind !== 'OPENING_USED' && kind !== 'USED_REVERSAL';
  }

  /** Nearest half-day. 1.3333 -> 1.5, 1.1 -> 1, 0.166 -> 0. Used for final balance rounding only. */
  static toHalfDays(days: number): number {
    return Math.round(days * 2) / 2;
  }

  /**
   * A stored date or timestamp as the LOCAL "YYYY-MM-DD", so two of them compare as strings.
   *
   * A bare date is taken as written: new Date() would read "2026-10-01" as UTC midnight, which
   * at UTC+5:30 is still the right day but is not worth relying on. A timestamp is parsed and
   * formatted in local time — an adjustment HR entered at 02:00 IST on 1 October is stored as
   * 30 September in UTC, and slicing the string would put it on the wrong side of a 1 October
   * joining date. An unparseable value returns '' and is never filtered.
   */
  static isoDay(value: string): string {
    const raw = String(value || '').trim();
    const match = /^(\d{4}-\d{2}-\d{2})$/.exec(raw);
    if (match) return match[1];
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /**
   * Leave types an employee only sees once they actually have some.
   *
   * Maternity and Paternity Leave grant 0 by default and are given out case by case, so
   * listing them on every employee's dashboard at 0 is noise.
   */
  private static readonly OPT_IN_TYPE_CODES = ['ML', 'PL'];

  /**
   * Whether this balance is worth showing the employee it belongs to.
   *
   * Replaces a hard `leaveTypeCode !== 'ML'` filter on the dashboard, which hid Maternity
   * Leave from every employee and manager unconditionally. That was a workaround for ML
   * being granted 180 days to everyone regardless of gender; now that it grants 0 by
   * default the workaround actively breaks things, because an employee HR *has* assigned
   * ML to could never see it — the grant landed in the balance and the dashboard dropped it.
   *
   * Keyed on whether there is anything to show rather than on the code or the viewer's role:
   * a granted allocation, a balance, days already taken or pending, or an HR adjustment all
   * make it real. So ML/PL stay hidden while they are 0 and appear the moment HR grants
   * them, by policy assignment or by adjustment, with no list of codes to maintain.
   */
  /**
   * The full pool a leave type's balance is measured against — the denominator in
   * "3 / 12 left", the "Granted" column on Reports, the base of every utilisation percent.
   *
   * One definition, because there were four and they disagreed. The employee dashboard and
   * the balance card used `granted + carryForward + compOffEarned`; Reports and the employee
   * profile drawer used bare `granted`. None of them included hrAdjustment, while `balance`
   * always has — so HR granting someone 5 days moved the balance and not the total beside
   * it, and the row rendered as "17 / 12 left". The grant had landed; the screen said
   * otherwise, which is indistinguishable from the grant having failed.
   *
   * Defined so that `balance === totalEntitlement - consumed - pending` holds by
   * construction. Expired grants are already out of hrAdjustment by the time it gets here.
   */
  static totalEntitlement(snapshot: LeaveBalanceSnapshot): number {
    if (snapshot.dojMissing) return 0;
    return this.toHalfDays(
      snapshot.granted + snapshot.carryForward + snapshot.compOffEarned + this.hrCreditOf(snapshot)
    );
  }

  /**
   * The credit half of the HR ledger — days added, never days taken away.
   *
   * The entitlement used to add the *net* hrAdjustment, which quietly made a deduction look
   * like a smaller entitlement instead of like leave taken. The system went live mid-year, so
   * leave taken before then was reconciled as negative adjustments — meaning an employee with
   * 10 CL who had taken 2.5 of them rendered as "granted 10 · used 0 · balance 7.5", three
   * numbers that cannot all be true at once. Splitting the ledger puts each half on the side
   * it belongs to: credits raise the entitlement, deductions count as used.
   *
   * Falls back to the net figure when the split fields are absent (a snapshot built before
   * they existed). The fallback preserves the invariant either way, because
   * `hrCredit - historicalUsed === hrAdjustment` holds by construction.
   */
  static hrCreditOf(snapshot: LeaveBalanceSnapshot): number {
    return snapshot.hrCredit ?? Math.max(0, snapshot.hrAdjustment);
  }

  /** Pre-go-live leave, as a positive number of days. See historicalUsed on the snapshot. */
  static historicalUsedOf(snapshot: LeaveBalanceSnapshot): number {
    return snapshot.historicalUsed ?? Math.max(0, -snapshot.hrAdjustment);
  }

  /**
   * Everything the employee has actually taken — the number every screen should label "Used".
   *
   * `consumed` alone only counts requests raised in the app, which is zero for every leave
   * taken before go-live. Reading the bare field is what produced "used 0" beside a balance
   * that was visibly short of the entitlement.
   *
   * Defined so that `totalEntitlement - totalUsed - pending === balance` holds by
   * construction, which is the property the screens rely on.
   */
  static totalUsed(snapshot: LeaveBalanceSnapshot): number {
    if (snapshot.dojMissing) return 0;
    return this.toHalfDays(snapshot.consumed + this.historicalUsedOf(snapshot));
  }

  /**
   * Used as a percentage of entitlement, for a progress bar or a utilisation figure.
   *
   * Lives here rather than at each call site because three screens had their own copy, all
   * dividing the bare `consumed` by the entitlement — so a bar sat empty for someone whose
   * only leave was pre-go-live. Zero entitlement yields 0, not NaN or Infinity: Comp-Off is
   * earned rather than granted and routinely divides by zero, as does any leave type before
   * HR grants it.
   */
  static utilisationPercent(snapshot: LeaveBalanceSnapshot): number {
    const entitlement = this.totalEntitlement(snapshot);
    if (entitlement <= 0) return 0;
    const percent = Math.round((this.totalUsed(snapshot) / entitlement) * 100);
    return Math.max(0, Math.min(100, percent));
  }

  static isBalanceVisibleToEmployee(snapshot: LeaveBalanceSnapshot): boolean {
    if (!this.OPT_IN_TYPE_CODES.includes(snapshot.leaveTypeCode.toUpperCase())) return true;
    return (
      snapshot.granted > 0 ||
      snapshot.balance > 0 ||
      snapshot.consumed > 0 ||
      snapshot.pending > 0 ||
      snapshot.hrAdjustment !== 0
    );
  }

  /**
   * Balances for one employee.
   *
   * Thin wrapper over computeSnapshotsFor: it only gathers the shared inputs (leave-type
   * master, the leave list, the employee record) and hands them over. All the actual
   * balance maths lives in one place so a page that needs many employees can batch the
   * fetches without re-implementing — and therefore drifting from — the arithmetic.
   */
  static async getBalances(employeeEmail: string, year: number): Promise<LeaveBalanceSnapshot[]> {
    // The leave TYPE master list (Leave Policies > Leave Types) is what determines which
    // leave types exist and get a balance at all — including newly-created custom types.
    // The older flat getPolicies()/LeavePolicy list is no longer consulted here; it only
    // remains for the legacy (unused) LeavePolicyModal.
    const leaveTypes = LeavePolicyService.getLeaveTypes().filter(t => t.isActive);
    // Comp-Off is refreshed here, not read here: computeSnapshotsFor reads it synchronously
    // out of the cache, so the cache has to be current before we get there. Without this the
    // Comp-Off line would read 0 for anyone who had not personally opened Apply Comp-Off in
    // this browser — including every approved claim their manager had just granted.
    // loadAllUsers, not getUserByEmail: that reads getCachedUsers(), a module-level cache
    // filled on first use and never invalidated (clearUserCache exists but nothing calls
    // it). The employee record supplies dateOfJoining, dateOfExit and employmentType — the
    // three inputs the accrual engine keys off — so a dashboard opened before HR corrected
    // a joining date kept computing against the old one for the rest of the session, while
    // Reports, which calls loadAllUsers directly, showed the corrected figure. Same source
    // on both sides now. Parallel with the rest, so it costs no extra round trip.
    const [leaves, users] = await Promise.all([
      LeaveService.listLeaves(),
      loadAllUsers(),
      CompOffService.refresh(),
      // Same reasoning as the Comp-Off refresh: computeSnapshotsFor reads the adjustment
      // ledger synchronously out of the cache, so the cache has to be current first. Without
      // it an employee never sees days HR granted them — the entry is on HR's machine.
      this.refreshAdjustments(),
      // And the configuration, for the same reason again: the leave-type list, the named
      // policies and the policy assignments are all read synchronously during the calculation,
      // so HR's entitlement changes only reach an employee's balance if the cache is current.
      LeavePolicyService.refreshConfig(),
    ]);
    // Domain-aware, like everywhere else: the caller may hold either of this person's
    // two addresses.
    const user = users.find(u => sameIdentity(u.email, employeeEmail));
    return this.computeSnapshotsFor(employeeEmail, user, year, leaveTypes, leaves);
  }

  /**
   * Balances for every employee in the directory, computed through the exact same code
   * path as getBalances.
   *
   * Reports used to derive its own figures in leaveReportService: it only built rows for
   * employees who happened to have a leave request, hardcoded ML to 180, never read
   * comp-off, and ignored HR adjustments entirely — so the Reports page and the Employee
   * Profile disagreed about the same person the moment HR granted anything. Sharing
   * computeSnapshotsFor removes the possibility of that divergence rather than patching
   * the symptoms.
   *
   * The leave list and the directory are fetched once for the whole run, not once per
   * employee, so this stays a fixed two round-trips regardless of headcount.
   */
  static async getBalancesForAll(
    year: number
  ): Promise<{ user: SimpleUser; snapshots: LeaveBalanceSnapshot[] }[]> {
    const leaveTypes = LeavePolicyService.getLeaveTypes().filter(t => t.isActive);
    // Still a fixed number of round-trips for the whole run — the Comp-Off refresh is one
    // scan for everybody, not one per employee. See getBalances for why it has to happen
    // before computeSnapshotsFor rather than inside it.
    const [leaves, users] = await Promise.all([
      LeaveService.listLeaves(),
      loadAllUsers(),
      CompOffService.refresh(),
      this.refreshAdjustments(),
      LeavePolicyService.refreshConfig(),
    ]);
    return users.map(user => ({
      user,
      snapshots: this.computeSnapshotsFor(user.email, user, year, leaveTypes, leaves),
    }));
  }

  /** The per-employee balance maths. Pure given its inputs — no fetching. */
  private static computeSnapshotsFor(
    employeeEmail: string,
    user: SimpleUser | undefined,
    year: number,
    leaveTypes: LeaveTypeMaster[],
    leaves: LeaveRequest[]
  ): LeaveBalanceSnapshot[] {
    const yearAdjustments = this.getAdjustments(employeeEmail, year);
    const compOffEarnedTotal = CompOffService.getTotalEarned(employeeEmail, year);
    const dateOfJoining = (user as any)?.dateOfJoining as string | undefined;
    const dateOfExit = (user as any)?.dateOfExit as string | undefined;
    const dojMissing = !dateOfJoining;

    const employmentType = (user as any)?.employmentType as string | undefined;

    /*
     * The Named Policy governing this employee.
     *
     * Employment Type is part of the lookup, which is the link between the "Intern Policy"
     * on the Leave Policies screen and somebody whose User Management record says Intern.
     * Without it a policy had no way to reach an employee at all: the other four scopes are
     * set by an assignment screen that no longer exists.
     *
     * It drives ML/CO/custom below, and now EL/CL/SL too — see capsFromPolicy.
     */
    const effectivePolicy = LeavePolicyService.getEffectivePolicyForEmployee({
      email: employeeEmail,
      department: (user as any)?.department,
      designation: (user as any)?.designation,
      location: (user as any)?.location,
      employmentType,
    });

    /*
     * Leave taken before the current Date of Joining is not charged to this entitlement.
     *
     * An intern converted to Full Time keeps the same login and gets a new Date of Joining,
     * and the grant above is rebuilt from that date under the new policy. The leave they took
     * as an intern belonged to the intern entitlement, which ended with the conversion — but it
     * is still in the same calendar year, so it was being subtracted from the new grant. Casual
     * Leave is where interns spend their days, which is why that card came out short while EL
     * and SL looked right.
     *
     * An ordinary employee has no requests dated before their joining date, so nothing changes
     * for them. With no Date of Joining every balance is 0 anyway, so nothing is filtered.
     */
    const joiningDay = dateOfJoining ? LeaveBalanceService.isoDay(dateOfJoining) : '';

    /*
     * HR adjustments entered before the current Date of Joining do not carry over either.
     *
     * Same reasoning as the leave requests below: a grant, deduction or opening balance HR
     * entered while the person was an intern belonged to the intern entitlement. Conversion is a
     * clean start — the new balance is the formula from the new joining date and nothing else —
     * so those rows are left out rather than having HR reverse each one by hand.
     *
     * Judged by when the row was ENTERED (updatedAt; adjustments are only ever created or
     * deleted, never edited). An ordinary employee's adjustments are all entered after they
     * joined, so nothing changes for them.
     */
    const adjustments = joiningDay
      ? yearAdjustments.filter(a => {
          const enteredOn = LeaveBalanceService.isoDay(a.updatedAt);
          return !enteredOn || enteredOn >= joiningDay;
        })
      : yearAdjustments;

    // Filter leaves for this employee and year
    const empLeaves = leaves.filter(l => {
      const lYear = new Date(l.startDate).getFullYear();
      const leaveDay = LeaveBalanceService.isoDay(l.startDate);
      if (joiningDay && leaveDay && leaveDay < joiningDay) return false;
      // Domain-aware: leave taken before the domain move is filed under the old address,
      // and dropping it here would silently understate the employee's used balance.
      return sameIdentity(l.employeeEmail, employeeEmail) && lYear === year;
    });

    const snapshots: LeaveBalanceSnapshot[] = [];

    /*
     * What this employee accrues against, from configuration only: the policy claiming them,
     * otherwise the Leave Types list. `leaveTypes` is already in hand here, so a run across
     * the company does not re-read it per person.
     */
    const rates =
      LeavePolicyService.ratesFromPolicy(effectivePolicy) ||
      LeavePolicyService.ratesFromLeaveTypes(leaveTypes);
    const leaveGrants = dojMissing
      ? { earnedMonths: 0, el: 0, cl: 0, sl: 0 }
      : computeAllLeaveGrants(dateOfJoining as string, year, dateOfExit, rates);

    for (const leaveType of leaveTypes) {
      const code = leaveType.code.toUpperCase();
      let consumed = 0;
      let pending = 0;

      for (const l of empLeaves) {
        const lTypeStr = String(l.type).toUpperCase();
        const matchesType = lTypeStr.includes(code) || (code === 'EL' && lTypeStr.includes('EARNED')) || (code === 'CL' && lTypeStr.includes('CASUAL')) || (code === 'SL' && lTypeStr.includes('SICK')) || (code === 'ML' && lTypeStr.includes('MATERNITY')) || (code === 'CO' && lTypeStr.includes('COMP'));

        if (!matchesType) continue;

        // leaveDaysFor is the single source of truth: 0.5 for a half-day session, otherwise
        // working days only. The previous inline formula counted calendar days with a
        // Math.max(1, …) floor, so a half-day cost a full day and Friday→Monday cost four.
        const days = leaveDaysFor(l);

        const statusStr = String(l.status).toLowerCase();
        if (statusStr === 'approved' || statusStr === 'cancellation requested') {
          // A cancellation that hasn't been approved yet keeps consuming balance:
          // the leave is still booked, and releasing the days at request time would
          // let someone free up balance simply by asking, before anyone agreed.
          consumed += days;
        } else if (statusStr === 'submitted' || statusStr === 'pending') {
          pending += days;
        }
      }

      // Aggregate HR adjustments (carry-forward is no longer part of the balance math — always 0)
      const typeAdjustments = adjustments.filter(a => a.leaveTypeCode.toUpperCase() === code);
      /*
       * Expired grants are dropped.
       *
       * An adjustment with `expiresOn` in the past stops counting — that is what makes the
       * optional expiration on Grant Leave a real rule rather than a stored date nobody reads.
       * Usable *through* the expiry day itself, matching Comp-Off. A deduction (negative
       * hrAdjustment) is treated the same way: if HR set an expiry on it, it lapses too, which
       * is the only reading consistent with the field meaning "applies until".
       */
      // CompOffService.todayIso, not toISOString().slice(0, 10) — the latter is the date in
      // UTC, which at UTC+5:30 still reads as yesterday until 05:30 local. A grant that
      // lapsed yesterday went on counting through the early morning, and one expiring today
      // was compared against the wrong day. Same reasoning, and the same helper, as the
      // Comp-Off expiry check; sharing it is what stops the two drifting apart again.
      const todayIso = CompOffService.todayIso();
      const unexpiredAdjustments = typeAdjustments.filter(a => !a.expiresOn || a.expiresOn >= todayIso);
      /*
       * Maternity and Paternity credited today are not spendable today.
       *
       * Same rule Comp-Off follows in CompOffService.usableRecords, applied at the equivalent
       * choke point: this is where the adjustment ledger turns into a number the eligibility
       * check will spend, so dropping it here reaches the balance card, the Apply form's
       * remaining-days figure and the submit-time validation together rather than only the
       * dropdown.
       *
       * Credits only. A deduction made today still applies immediately — withholding it would
       * move the balance *up*, which is the wrong direction for a rule about not spending
       * something too early.
       */
      const liveAdjustments = unexpiredAdjustments.filter(
        a => !(isSameDayCreditCode(code) && a.hrAdjustment > 0 && creditedToday(a, todayIso))
      );
      const hrAdjSum = liveAdjustments.reduce((acc, a) => acc + a.hrAdjustment, 0);
      /*
       * The same ledger, split by which side of the balance each row belongs to, so the display
       * can put every movement where it belongs.
       *
       * Split by *kind*, not by sign. Sign alone cannot tell a reduction in entitlement from
       * leave taken before go-live — both are negative, and reading them the same way makes one
       * of the two wrong on screen whichever way it is read. See LeaveAdjustmentKind. Rows with
       * no kind fall back to the sign, which is what they always did.
       *
       * Summed from the rows rather than derived from `hrAdjSum`, because the net loses the
       * distinction the moment someone has both — an employee granted 5 and docked 2 has taken
       * 2 days, which a net of +3 cannot express.
       *
       * `liveAdjustments`, the same list the net uses: expired rows and ML/PL credits withheld
       * until tomorrow are already out of it. Sharing the list is what guarantees
       * hrCredit - historicalUsed === hrAdjSum, and therefore the invariant
       * entitlement - used - pending === balance. Note both halves are signed: an entitlement
       * reduction makes hrCredit smaller, and a used-reversal makes historicalUsed smaller.
       */
      const entitlementRows = liveAdjustments.filter(a => LeaveBalanceService.affectsEntitlement(a));
      const usedRows = liveAdjustments.filter(a => !LeaveBalanceService.affectsEntitlement(a));
      const hrCredit = entitlementRows.reduce((acc, a) => acc + a.hrAdjustment, 0);
      // Negated: a used-side row carries negative days when it adds to what was used.
      const historicalUsed = usedRows.reduce((acc, a) => acc - a.hrAdjustment, 0);

      /*
       * Surfaced so the employee is warned before the days lapse rather than after.
       *
       * Two sources, because a leave type can hold days that expire for two different
       * reasons, and Comp-Off routinely holds both at once:
       *   - HR grants made in HR Operations > Grant Leave, which carry an optional
       *     "expires on" the admin typed;
       *   - for Comp-Off, the days themselves, which lapse a configured window (45 by
       *     default) after the date worked — see CompOffService.getExpiringDays.
       *
       * Only credits: an expiry on a deduction still lapses (above), but "expiring" is not
       * a meaningful thing to show someone about days taken off them.
       */
      const expiringGrants = [
        ...liveAdjustments
          // Entitlement credits only. "3 days expiring on the 30th" describes days the employee
          // could still spend; a used-side row is a record of days already gone.
          .filter(a => a.expiresOn && a.hrAdjustment > 0 && LeaveBalanceService.affectsEntitlement(a))
          .map(a => ({ days: a.hrAdjustment, expiresOn: a.expiresOn as string })),
        ...(code === 'CO' ? CompOffService.getExpiringDays(employeeEmail, year) : []),
      ].sort((x, y) => x.expiresOn.localeCompare(y.expiresOn));
      const compOffSum = code === 'CO' ? compOffEarnedTotal : typeAdjustments.reduce((acc, a) => acc + a.compOffEarned, 0);

      // --- Grant calculation ---
      // EL, CL, SL are formula-driven via leaveAccrualEngine (earnedMonths-based).
      // ML, CO, and any custom types keep their existing policy-based monthly proration.
      let granted = 0;
      if (!dojMissing) {
        if (code === 'EL') {
          granted = leaveGrants.el;
        } else if (code === 'CL') {
          granted = leaveGrants.cl;
        } else if (code === 'SL') {
          granted = leaveGrants.sl;
        } else if (code === 'CO') {
          /*
           * Comp-Off is earned, never granted.
           *
           * A day exists because someone worked a holiday or a weekend and it was approved;
           * compOffSum below carries exactly those days, already filtered for expiry. Running
           * CO through the policy proration underneath as well would ADD an annual allowance
           * on top of the earned days — the balance formula adds both — so every employee
           * would receive Comp-Off nobody worked for.
           *
           * Latent rather than active today only because grantedPerYear is 0 for CO in the
           * shipped defaults and the config table is empty. It becomes real the moment
           * anyone types a number into Comp-Off in Leave Policies, or assigns a Named Policy
           * with a CO allocation — neither of which should be capable of inventing days.
           */
          granted = 0;
        } else {
          // ML, CO, and custom leave types: monthly-prorated from a grantedPerYear value.
          // If HR has assigned this employee a Named Policy with an allocation for this
          // leave type, that allocation wins over the global default — this is what makes
          // Leave Assignment actually change the employee's real grant instead of only
          // logging an audit entry.
          const assignedAllocation = effectivePolicy?.allocations.find(
            a => a.leaveTypeCode.toUpperCase() === code
          );
          /*
           * Per month, at whichever frequency supplied the number: a policy allocation is
           * read at that policy’s accrual frequency, while a leave-type default is annual
           * by definition. Maternity on a MONTHLY policy means that many days a month, the
           * same way Casual Leave does — one reading of the ACCRUAL column, not two.
           */
          const monthlyRate = assignedAllocation
            ? LeavePolicyService.perMonthRate(
                assignedAllocation.grantedPerYear,
                effectivePolicy?.accrualFrequency
              )
            : LeavePolicyService.perMonthRate(leaveType.grantedPerYear, 'YEARLY');
          // The month-credit count comes from the engine rather than being recomputed here.
          //
          // This block used to carry its own copy of the same rule — joining-month half/full
          // credit, whole months after it, capped at the exit month — and the two copies had
          // to be corrected in lockstep. They were not: when the joining-year rule changed to
          // run through December instead of stopping at the current month, a second copy here
          // would have left ML and Comp-Off prorated to today while EL/CL/SL covered the full
          // year, for the same employee on the same screen. computeEarnedMonths returns
          // exactly the credit count this needs for every case (joining year, later years,
          // exit-year cap, pre-joining and post-exit zeroes), so there is nothing left to
          // duplicate.
          const credits = computeEarnedMonths(dateOfJoining as string, year, dateOfExit);
          granted = this.toHalfDays(monthlyRate * Math.min(12, credits));
        }
      }

      // Formula: Balance = Granted - Approved Leave - Pending + Comp-Off Earned + HR Adjustments
      const balance = dojMissing
        ? 0
        : this.toHalfDays(granted - consumed - pending + compOffSum + hrAdjSum);

      snapshots.push({
        id: `snap-${employeeEmail}-${code}-${year}`,
        employeeEmail,
        year,
        leaveTypeCode: code,
        granted: dojMissing ? 0 : granted,
        consumed,
        pending,
        carryForward: 0,
        compOffEarned: compOffSum,
        hrAdjustment: hrAdjSum,
        // Both halves stored raw, like the net `hrAdjustment` above and unlike `granted`:
        // zeroing one of them for a missing DOJ would break
        // `hrCredit - historicalUsed === hrAdjustment`. The DOJ case is handled where it
        // matters instead — totalEntitlement and totalUsed both return 0 without one.
        hrCredit,
        historicalUsed,
        balance,
        // Always true, and not by choice: gender is not stored on the employee record
        // anywhere — not in Cognito, not in UserProfiles, not on SimpleUser — so a
        // FEMALE/MALE policy cannot be evaluated. LeaveBalanceCard already renders an
        // "ineligible" card when this is false, so the moment gender exists (UserProfiles
        // is the right home, alongside shift and dateOfJoining) this becomes:
        //   genderEligibility === 'ALL' || genderEligibility === employee.gender
        // Until then Maternity Leave is shown to every employee regardless of gender.
        isEligible: true,
        lastCalculatedAt: new Date().toISOString(),
        dojMissing,
        expiringGrants,
      });
    }

    return snapshots;
  }

  /**
   * Year-end reset — NO carry-forward. Balances for `fromYear + 1` are computed fresh and
   * dynamically by getBalances() (DOJ-prorated accrual for the new year), so there is no
   * persisted "carried forward" state to compute or store here. This method's job is limited
   * to: (1) never creating carry-forward adjustment records, and (2) logging an audit trail
   * per employee (and a summary entry) so the reset is traceable.
   */
  static async performYearEndRollover(fromYear: number, updatedBy: string): Promise<void> {
    const nextYear = fromYear + 1;
    const allEmails = await getAllUserEmails();

    for (const email of allEmails) {
      LeaveAuditService.logAction(
        `rollover-${fromYear}-${nextYear}-${email}`,
        'Year-End Reset — No Carry-Forward',
        updatedBy,
        `${fromYear}`,
        `${nextYear}`,
        `Balances for ${nextYear} reset to DOJ-prorated grant with no carry-forward from ${fromYear}`,
        email
      );
    }

    LeaveAuditService.logAction(
      `rollover-${fromYear}-${nextYear}`,
      'Automated Year-End Rollover Completed',
      updatedBy,
      `${fromYear}`,
      `${nextYear}`,
      `Year-end reset for ${nextYear} completed — no carry-forward applied (policy removed)`
    );

    window.dispatchEvent(new Event('leave-balance-updated'));
  }

  /**
   * Resolves the affected employee emails for a policy assignment scope and logs an
   * audit trail entry per employee/allocation. The assignment itself takes effect
   * immediately and automatically the next time getBalances() runs for an affected
   * employee — see getEffectivePolicyForEmployee() in leavePolicyService.ts, which
   * getBalances() consults for ML/CO/custom leave types. (EL/CL/SL are never affected
   * by any assignment — they stay universal and formula-driven.) This function does
   * not need to write balance data itself; it exists purely for the audit trail.
   * Used by the Leave Assignment UI (Leave Policies > Leave Assignment).
   */
  static async generateBalancesForAssignment(
    policy: NamedLeavePolicy,
    scopeType: AssignmentScopeType,
    scopeValue: string,
    assignedBy: string,
    year: number = new Date().getFullYear()
  ): Promise<string[]> {
    const allUsers = await loadAllUsers();
    let affected: string[] = [];

    if (scopeType === 'EMPLOYEE') {
      affected = [scopeValue];
    } else if (scopeType === 'DEPARTMENT') {
      affected = allUsers.filter(u => u.department?.toLowerCase() === scopeValue.toLowerCase()).map(u => u.email);
    } else if (scopeType === 'DESIGNATION') {
      affected = allUsers.filter(u => u.designation?.toLowerCase() === scopeValue.toLowerCase()).map(u => u.email);
    } else if (scopeType === 'LOCATION') {
      affected = allUsers.filter(u => u.location?.toLowerCase() === scopeValue.toLowerCase()).map(u => u.email);
    }

    for (const email of affected) {
      for (const allocation of policy.allocations) {
        LeaveAuditService.logAction(
          `assign-${policy.id}-${email}`,
          'Leave Policy Assignment — Balance Generated',
          assignedBy,
          undefined,
          `${allocation.grantedPerYear} day(s) ${allocation.leaveTypeCode} under "${policy.name}"`,
          `Policy "${policy.name}" assigned to ${scopeType.toLowerCase()} "${scopeValue}" for ${year}`,
          email
        );
      }
    }

    window.dispatchEvent(new Event('leave-balance-updated'));
    window.dispatchEvent(new Event('storage'));
    return affected;
  }
}
